export abstract class DateService {

  /**
   * @param  date Date object to modify
   * @returns Date object with time part set to zero
   *
   * @description sets all time fields to zero
   */
  static stripTime(date: Date): Date {
    if (Object.prototype.toString.call(date) === '[object Date]' && date !== null) {
      date.setHours(0, 0, 0, 0);
    }
    return date;
  }

  static isDate(date: unknown): boolean {
    return Object.prototype.toString.call(date) === '[object Date]';
  }

  /**
   * Calculates time difference using UTC timezone
   *
   * @param date1
   * @param date2
   * @returns Time difference in milliseconds
   */
  static getUTCTimeDifferenceInMilliseconds(date1: Date, date2: Date): number {
    return DateService.getUTCMilliseconds(date2) - DateService.getUTCMilliseconds(date1);
  }

  /**
   * @param date1 Date or numeric equivalent
   * @param date2 Date or numeric equivalent
   * @returns  boolean  true if date year, month and date are equal.
   *
   * @description
   *  Compares date objects ignoring time part
   *
   */
  static isSameDay(date1: Date, date2: Date): boolean {
    date1 = DateService.convertToDateIfPossible(date1);
    date2 = DateService.convertToDateIfPossible(date2);
    if (Object.prototype.toString.call(date1) === '[object Date]' && Object.prototype.toString.call(date2) === '[object Date]') {
      return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
    }
    return false;
  }

  /**
   * Converts value to Date if it is milliseconds
   *
   * @param value
   */
  static convertToDateIfPossible(value: number | Date): Date {
    return typeof value === 'number' ? new Date(value) : value;
  }

  /***
   * @param date Local date
   */
  static getUTCMilliseconds(date: Date): number {
    return Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds());
  }

  /**
   * @param date A given base date
   * @param days Any number representing days in past (negative) or future (positive)
   */
  static getDateFrom(date: Date, days: number): Date {
    const tmpDate = new Date(date);
    tmpDate.setDate(tmpDate.getDate() + days);
    return tmpDate;
  }

  static setDateRange(customRange: { startDate?: Date; endDate?: Date }, minDate: Date, maxDate: Date):  void {

    if (maxDate !== undefined) {
      maxDate = DateService.adjustForWeekend(DateService.convertDate(maxDate), -1) ?? maxDate;

      if (
        (customRange.endDate === undefined) ||
        (customRange.endDate > maxDate)
      ) {
        customRange.endDate = maxDate;
      }
    }

    if (minDate !== undefined && customRange.endDate !== undefined) {
      const yearBefore = DateService.getYearBefore(customRange.endDate);
      const yearBeforeEndDate = DateService.adjustForWeekend(yearBefore ?? customRange.endDate, 1) ?? minDate;
      if (
        (customRange.startDate === undefined) ||
        (customRange.startDate < minDate)
      ) {
        customRange.startDate = yearBeforeEndDate > minDate ? yearBeforeEndDate : minDate;
      }
    }
  }

  /**
   * This function subtracts a year from date argument
   *
   * @param date Variable to subtract from
   * @returns Subtracted date
   */
  static getYearBefore(date: Date): Date | undefined {
    let clone: Date | undefined;
    if (DateService.isDate(date)) {
      clone = new Date(date.getTime());
      clone.setFullYear(clone.getFullYear() - 1);
      return clone;
    }
  }

  /**
   * This function makes adjustment for weekend
   *
   * @param date Date to make adjustment from
   * @param days Number of days
   * @returns Adjusted date
   */
  static adjustForWeekend(date: Date, days: number): Date | undefined {
    let day: number;
    let increment: number;
    if (DateService.isDate(date)) {
      day = date.getDay();
      if (day === 0 || day === 6) {
        if (days < 0) {
          increment = day === 0 ? -2 : -1;
        } else {
          increment = day === 0 ? 1 : 2;
        }
        date.setDate(date.getDate() + increment);
      }
      return date;
    }
  }

  /**
   * This function converts argument into date object if possible
   *
   * @param date Variable to convert to date object
   * @returns Converted date
   */
  static convertDate(date: unknown): Date {
    return DateService.isDate(date) ? date as Date : new Date(date as string | number);
  }

  static  isWeekend(date: Date): boolean {
    if (DateService.isDate(date)) {
      const day = date.getDay();
      return day === 6 || day === 0;
    }
    return false;
  }

  static getDaysDifference(dateA: Date | null | undefined, dateB: Date | null | undefined): number | null {
    if (dateA !== null && dateA !== undefined && dateB !== null && dateB !== undefined) {
      const daysDiff: number = new Date(dateB).getTime() - new Date(dateA).getTime();
      return Math.ceil(daysDiff / (1000 * 3600 * 24));
    }
    return null;
  }
}
